import store from '@/store'
import router from '@/router'

const defaultEndpoint = '/travels'

const defaultContinents = [
  { id: 1, alias: 'Europe', name: 'Europe'  },
  { id: 2, alias: 'Asia', name: 'Asia'  },
  { id: 3, alias: 'Africa', name: 'Africa'  },
  { id: 4, alias: 'North America', name: 'North America'  },
  { id: 5, alias: 'South America', name: 'South America'  },
  { id: 6, alias: 'Australia', name: 'Oceania'  },
  { id: 7, alias: 'Antarctica', name: 'Antarctica'  },
]

const state = {
  travelEntities: [],
  continents: defaultContinents, // Keep original order as fallback
  continentMap: {}, // Map continent names to their data
  loading: false,
  selectedContinent: null,
  currentType: 'continent',
  currentValue: null,
  searchQuery: null,
  visitedFilter: false,
  currentUser: null, // Track current user to detect changes
}

const getters = {
  travelEntities: (state) => state.travelEntities,
  continents: (state) => state.continents,
  loading: (state) => state.loading,
  selectedContinent: (state) => state.selectedContinent,
  currentType: (state) => state.currentType,
  currentValue: (state) => state.currentValue,
  searchQuery: (state) => state.searchQuery,
  visitedFilter: (state) => state.visitedFilter,

  visitedCount: (state) => {
    return state.travelEntities.filter(entity => entity.visited).length
  },

  pageTitle: (state) => {
    if (state.currentType === 'continent') {
      return 'Countries'
    } else if (state.currentType === 'country') {
      return 'Cities'
    }
    return 'Travel Entities'
  },

  entityTypeLabel: (state) => {
    if (state.currentType === 'continent') {
      return 'countries'
    } else if (state.currentType === 'country') {
      return 'cities'
    }
    return 'items'
  },
}

const actions = {
  async fetchTravelEntities({ commit, state }, request = {}) {
    commit('setLoading', true)

    try {
      const params = {
        type: request.type || state.currentType,
        parent_id: request.parent_id || state.currentValue,
      }

      // Add username if not own profile
      if (request.username) {
        params.username = request.username
      }

      // Add search query if provided
      if (request.search || state.searchQuery) {
        params.search = request.search || state.searchQuery
      }

      // Add visited filter - send boolean value directly
      if (request.visited !== undefined) {
        params.visited = request.visited
      } else if (state.visitedFilter) {
        params.visited = state.visitedFilter
      }

      const response = await this._vm.$api.get(defaultEndpoint, { params })

      if (response.data && response.data.data) {
        const entities = response.data.data.map(item => item.attributes)
        commit('setTravelEntities', entities)
      }

      return response
    } catch (error) {
      console.error('Error fetching travel entities:', error)
      commit('setTravelEntities', [])
      return { data: null }
    } finally {
      commit('setLoading', false)
    }
  },

  async fetchContinents({ commit, state }, username = null) {
    try {
      const params = {
        type: 'continent',
        // No parent_id for continents as they are top-level
      }

      // Add username if provided (for other user's profiles)
      if (username) {
        params.username = username
      }

      const response = await this._vm.$api.get(defaultEndpoint, { params })

      if (response.data && response.data.data) {
        // Create a map of API continents by name
        const apiContinentMap = {}
        response.data.data.forEach(item => {
          apiContinentMap[item.attributes.title] = {
            id: item.id,
            name: item.attributes.title,
            attributes: item.attributes
          }
        })

        // Keep original order but update with API data
        const orderedContinents = state.continents.map(continent => {
          return apiContinentMap[continent.name] || continent
        })

        commit('setContinents', { continents: orderedContinents, continentMap: apiContinentMap })
      }

      return response
    } catch (error) {
      console.error('Error fetching continents:', error)
      commit('setContinents', { continents: state.continents, continentMap: {} })
      return { data: null }
    }
  },

  async loadDefaultContinent({ commit }) {
    try {
      const response = await this._vm.$api.get('/profile')
      const defaultContinent = response.data.relations.default_continent

      if (defaultContinent) {
        commit('setSelectedContinent', defaultContinent.id)
        commit('setCurrentType', 'continent')
        commit('setCurrentValue', defaultContinent.id)
      }

      return response
    } catch (error) {
      console.error('Error loading default continent:', error)
      return { data: null }
    }
  },

  async initializeForUser({ commit, dispatch }, username) {
    // Check if user changed and reset state if needed
    commit('checkAndResetForUser', username)

    // Fetch continents for this user
    await dispatch('fetchContinents', username)

    // Load default continent
    await dispatch('loadDefaultContinent')
  },

  setCurrentType({ commit }, type) {
    commit('setCurrentType', type)
  },

  setCurrentValue({ commit }, value) {
    commit('setCurrentValue', value)
  },

  setSelectedContinent({ commit }, continentId) {
    commit('setSelectedContinent', continentId)
  },

  setSearchQuery({ commit }, query) {
    commit('setSearchQuery', query)
  },

  setVisitedFilter({ commit }, filter) {
    commit('setVisitedFilter', filter)
  },

  resetState({ commit }) {
    commit('resetState')
  },
}

const mutations = {
  setTravelEntities(state, entities) {
    state.travelEntities = entities
  },

  setLoading(state, loading) {
    state.loading = loading
  },

  setSelectedContinent(state, continentId) {
    state.selectedContinent = continentId
  },

  setCurrentType(state, type) {
    state.currentType = type
  },

  setCurrentValue(state, value) {
    state.currentValue = value
  },

  setSearchQuery(state, query) {
    state.searchQuery = query
  },

  setVisitedFilter(state, filter) {
    state.visitedFilter = filter
  },

  setContinents(state, { continents, continentMap }) {
    state.continents = continents
    state.continentMap = continentMap
  },

  setCurrentUser(state, username) {
    state.currentUser = username
  },

  checkAndResetForUser(state, username) {
    // If user changed, reset the state
    if (state.currentUser && state.currentUser !== username) {
      state.travelEntities = []
      // Don't reset continents to empty - preserve the default list
      state.continents = defaultContinents
      state.continentMap = {}
      state.selectedContinent = null
      state.currentType = 'continent'
      state.currentValue = null
      state.searchQuery = null
      state.visitedFilter = false
    }
    state.currentUser = username
  },

  resetState(state) {
    state.travelEntities = []
    state.continents = []
    state.loading = false
    state.selectedContinent = null
    state.currentType = 'continent'
    state.currentValue = null
    state.searchQuery = null
    state.visitedFilter = false
    state.currentUser = null
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
